import { Button } from "../../components/ui/button";
import { FileDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { api } from "../../services/api";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store/store";
import { deleteGenericJob, startGenericJob } from "../../store/jobsSlice";
import { GenericJobService } from "../../services/job/GenericJobService";
import { apiConfig } from "../../config/api";
import { analytics } from "../../analytics";
import { useToast } from "../../components/hooks/use-toast";
import { awaitJob } from "../../services/job/awaitJob";
import { resetPendingChanges } from "../../store/flowSlice";
import beforeUpdateFormatter from "../../services/beforeUpdateFormatter";
import { useTranslation } from "../../i18n/hooks";

type ExportFormat = 'csv' | 'xlsx' | 'jsonl';

type ExportLocallyButtonProps = {
  enrichmentJobId: string;
};

const ExportLocallyButton = ({ enrichmentJobId }: ExportLocallyButtonProps) => {
  const { currentCollection, currentUser } = useSelector((state: RootState) => state.user);
  const [loading, setLoading] = useState(false);
  const [requested, setRequested] = useState(false);
  const [lastFormat, setLastFormat] = useState<ExportFormat | null>(null);
  const job = useSelector(
    (state: RootState) => state.jobs.activeGenericJobs[enrichmentJobId]
  );
  const {enrichmentPendingChanges} = useSelector((state: RootState) => state.flow);
  const { t } = useTranslation();

  useEffect(() => {
    if (job?.status === "completed" && requested) {
      continueExport();
    }
  }, [job]);

  const dispatch = useDispatch();
  const {toast} = useToast();

  const continueExport = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiConfig.baseUrl}/utils/get_file_from_bucket?gs_uri=${job.payload.export_uri}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Get filename from Content-Disposition header or fallback
      const defaultExtension = lastFormat || 'csv';
      const filename = response.headers.get('Content-Disposition')?.split('filename=')[1] || `export.${defaultExtension}`;
      
      // Create a ReadableStream from the response
      const reader = response.body?.getReader();
      const stream = new ReadableStream({
        async start(controller) {
          while (true) {
            const {done, value} = await reader!.read();
            if (done) break;
            controller.enqueue(value);
          }
          controller.close();
          reader!.releaseLock();
        }
      });
      
      // Create response from stream
      const newResponse = new Response(stream);
      const blob = await newResponse.blob();
      
      // Create download link and trigger
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      analytics.sendEnhancementJobExportedEvent({
        jobId: enrichmentJobId,
        email: currentUser?.email ?? "unknown",
        destination: "csv"
      })
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);

      dispatch(deleteGenericJob(enrichmentJobId));
      
      const pendingChanges = enrichmentPendingChanges.filter(
        (change) => change.enrichmentJobId === enrichmentJobId
      );
      if(pendingChanges.length > 0){
        dispatch(resetPendingChanges())
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      toast({
        title: t('exportButton.export_failed'),
        description: error instanceof Error ? error.message : t('exportButton.unknown_error'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }


  const handleExport = async (format: ExportFormat) => {
    setRequested(true);
    try {
      setLastFormat(format);
      setLoading(true);
      const pendingChanges = enrichmentPendingChanges.filter(
        (change) => change.enrichmentJobId === enrichmentJobId
      );
      
      if (pendingChanges.length > 0) {
        if(!currentCollection?.collection?.tableId){
          throw new Error("Table id not found in current collection.")
        }
        const { error, success, data } = await api.postUpdateEnrichedCollection(
          {
            table_id: currentCollection?.collection?.tableId,
            job_id: enrichmentJobId,
            update_fields:beforeUpdateFormatter(pendingChanges.map((change) => ({
              field_name: change.fieldName,
              field_value: change.fieldValue,
              product_id: change.productId,
              source_id: change.sourceId,
            }))),
          }
        );
        if(error){
          throw new Error(`Couldn't update the product. ${error.message}`);
        }
        if(!data?.job_id){
          throw new Error("No job ID found in update job response");
        }
        
        await awaitJob(data.job_id);
      }

      // if(job?.status === "completed"){
      //   continueExport();
      //   return;
      // }
      
      if (!currentCollection?.collection || !currentCollection?.collection?.tableId) {
        throw new Error("No active collection found");
      }
      const { data } = await api.exportToCSV({
        enrichmentJobId,
        inputTableId: currentCollection?.collection?.tableId,
        format,
      });
      if (!data?.job_id) {
        throw new Error("No job ID found in response");
      }
      dispatch(
        startGenericJob({
          identifier: enrichmentJobId,
          trackingId: data.job_id,
        })
      );
      GenericJobService.getInstance().ensurePolling();
    } catch (e) {
      console.log(e);
      setLoading(false)
      toast({
        title: t('exportButton.export_failed'),
        description: typeof e === "string" ? e : (e as Error).message,
        variant: "destructive",
      });
    }
  };
  const jobLoading = job?.status === "running";
  const buttonLoading = loading || jobLoading;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          disabled={buttonLoading}
          className="gap-2"
          data-testid="local-export-button"
        >
          {buttonLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <FileDown className="w-4 h-4" />
          )}
          {buttonLoading
            ? t("exportButton.exporting_progress", {
                progress: job?.progress ?? 0,
              })
            : t("exportButton.export_locally")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48 z-[50000]">
        <DropdownMenuItem
          data-testid="csv-export-button"
          onClick={() => handleExport("csv")}
          className="hover:bg-slate-100 cursor-pointer"
        >
          {t("exportButton.export_as_csv")}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleExport("xlsx")}
          className="hover:bg-slate-100 cursor-pointer"
        >
          {t("exportButton.export_as_excel")}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleExport("jsonl")}
          className="hover:bg-slate-100 cursor-pointer"
        >
          {t("exportButton.export_as_jsonl")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ExportLocallyButton;
